/* eslint-disable max-len */
import { CircularProgress, Grid, makeStyles, Paper, Theme, useTheme, Icon, darken } from '@material-ui/core';
import { TimeSpan, useModePanelStyle } from '@moderepo/mode-app-components';
import { SmartModuleType, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import moment from 'moment';
import React, { useMemo, useState, useCallback, useEffect, CSSProperties } from 'react';
import { CardHighlightStyle, EntityNumericMetricsCustomSettings, EntityUIComponentCollection, EntityUIComponentProps, FetchEntityTimeSpanDataParams,
    filterMetricsDefinitionsByMetricsExclusionCustomSettings, getNumericMetricCustomSettings, GridCustomSettings, isCardHighlightStyle,
    isEntityNumericMetricsCustomSettings, isFetchEntityTimeRangeDataParams, isFetchEntityTimeSpanDataParams, isGridCustomSettings,
    isKPIMetric, isMetricsDefinitionAndMetricsExclusionCustomSettings, MetricsDefinitionsAndMetricsExclusionCustomSettings,
    useSelectTranslatedEntityClasses } from '..';
import { ExtGrid } from '../..';
import { NO_VALUE } from '../../../utils/constants';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';
import { SUPPORT_TIME_SPANS } from '../../misc';
import { SparklineChart } from '../../time-series';
import { getEntityMetricGroupPreviewData, createFetchEntityMetricDataParamsGroup, MetricPreviewData, KPIStatus } from '../utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        metricCard: {
            display      : 'flex',
            height       : '100%',
            flexDirection: 'column',
            alignItems   : 'center',
            overflow     : 'hidden',
            '&.clickable': {
                cursor: 'pointer',
            },

            '&.highlight': {
                '&.bar': {
                    borderBottom: '6px solid transparent',

                    '&.status-neutral': {
                        borderBottomColor: darken(theme.palette.background.paper, 0.2),
                    },

                    '&.status-good': {
                        borderBottomColor: theme.palette.success.light,
                    },

                    '&.status-bad': {
                        borderBottomColor: theme.palette.error.light,
                    },
                },
                '&.border': {
                    border            : '6px solid transparent',
                    '&.status-neutral': {
                        borderColor: darken(theme.palette.background.paper, 0.2),
                    },

                    '&.status-good': {
                        borderColor: theme.palette.success.light,
                    },

                    '&.status-bad': {
                        borderColor: theme.palette.error.light,
                    },
                },
                '&.background': {
                    border            : '6px solid transparent',
                    '&.status-neutral': {
                        background: darken(theme.palette.background.paper, 0.2),
                        color     : theme.palette.text.primary,
                    },

                    '&.status-good': {
                        background: theme.palette.success.light,
                        color     : theme.palette.success.contrastText,
                    },

                    '&.status-bad': {
                        background: theme.palette.error.light,
                        color     : theme.palette.error.contrastText,
                    },
                },
            },
        },

        panelHeader: {
            display          : 'flex',
            justifyContent   : 'center',
            padding          : theme.spacing(1),
            fontWeight       : 'bold',
            textAlign        : 'center',
            fontSize         : 20,
            width            : '100%',
            borderBottomWidth: 2,
        },

        panelContent: {
            width        : '100%',
            height       : '100%',
            padding      : theme.spacing(1),
            display      : 'flex',
            flexDirection: 'column',
        },

        metricInfo: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            width         : '100%',
            flex          : 1,
        },

        metricName: {
            fontWeight: 'bold',
            alignSelf : 'flex-start',
        },

        metricChartAndValueContainer: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
            padding       : theme.spacing(1, 0),
            height        : '100%',
            '& .chart-col': {
                textAlign : 'right',
                width     : '50%',
                '& .chart': {
                    display: 'inline-block',
                    width  : 50,
                    height : 25,
                },
            },
            '& .value-col': {
                fontSize    : 24,
                fontWeight  : 'bold',
                textAlign   : 'center',
                whiteSpace  : 'nowrap',
                paddingRight: theme.spacing(2),
            },
            '& .unit-col': {
                fontSize  : 20,
                fontWeight: 'bold',
                textAlign : 'left',
                whiteSpace: 'nowrap',
            },
            '&.has-chart': {
                justifyContent: 'space-between',
                '& .value-col': {
                    paddingLeft: theme.spacing(2),
                },
                '& .unit-col': {
                    width: '50%',
                },
            },
        },

        previousMetricValueDisplay: {
            fontWeight: 'bold',
        },

        [theme.breakpoints.up('md')]: {
            panelHeader: {
                fontSize: 22,
            },
            panelContent: {
                padding: theme.spacing(1.5),
            },
            metricName: {
                fontSize: 14,
            },
            metricChartAndValueContainer: {
                padding       : theme.spacing(1.5, 0),
                '& .value-col': {
                    fontSize: 28,
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            panelHeader: {
                fontSize: 25,
            },
            panelContent: {
                padding: theme.spacing(1.5),
            },
            metricName: {
                fontSize: 16,
            },
            metricChartAndValueContainer: {
                padding       : theme.spacing(1.5, 0),
                '& .value-col': {
                    fontSize: 32,
                },
            },
        },
    };
}, { name: 'EntityAggregatedMetricsCardsViewUIComponent', index: 1 });



/**
 * Custom settings for EntityAggregatedMetricsCardsViewUIComponent. These settings can be configured from Entity Class's uiSettings
 */
export interface EntityAggregatedMetricsCardsViewUIComponentCustomSettings extends
    MetricsDefinitionsAndMetricsExclusionCustomSettings, GridCustomSettings, EntityNumericMetricsCustomSettings {

    // Because this component only works with time span, we need the default Time Span to use if this component is NOT used in a view
    // that use TimeSpan. If this is not provided and this component is not used in a view that use time span then the component will use
    // TimeSpan.DAY by default.
    readonly defaultTimeSpan?: TimeSpan.DAY | TimeSpan.WEEK | TimeSpan.MONTH | undefined;

    // The aggregation to use for fetching data for metrics that are NOT KPI metrics. KPI metrics already has aggregation set.
    readonly defaultAggregation?: TimeSeriesAggregation | undefined;

    // Whether or not to show the previous value
    readonly showPreviousValue?: boolean | undefined;

    // Whether or not to show a historical data in a small sparkline chart. The default is FALSE unless specifically set to true
    readonly showHistoricalChart?: boolean | undefined;

    /**
     * To highlight the card based on some event record field value. The card can be highlighted in different style, bar, border, or background
     */
    readonly highlightCardStyle?: CardHighlightStyle | undefined;

    /**
     * The style to apply to each Card's outer container element
     */
    readonly cardContainerStyle?: CSSProperties | undefined;
}


/**
* type guard function to check if an object is a type of EntityAggregatedMetricsCardsViewUIComponentCustomSettings
*/
const isEntityAggregatedMetricsCardsViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityAggregatedMetricsCardsViewUIComponentCustomSettings => {
    const settings = obj as EntityAggregatedMetricsCardsViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultTimeSpan, false, false, SUPPORT_TIME_SPANS)) {
        return errorLogger?.logError({
            message: `"defaultTimeSpan" must be undefined or one of the following values [${
                SUPPORT_TIME_SPANS.map((value) => {
                    return `"${value}"`;
                }).join(', ')
            }]`,
            value: settings.defaultTimeSpan,
            path : [...configPath, 'defaultTimeSpan'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultAggregation, false, false, Object.values(TimeSeriesAggregation))) {
        return errorLogger?.logError({
            message: `"defaultAggregation" must be undefined or one of the following values [${
                Object.values(TimeSeriesAggregation).map((value) => {
                    return `"${value}"`;
                }).join(', ')
            }]`,
            value: settings.defaultAggregation,
            path : [...configPath, 'defaultAggregation'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showPreviousValue)) {
        return errorLogger?.logError({
            message: '"showPreviousValue" must be undefined or a boolean.',
            value  : settings.showPreviousValue,
            path   : [...configPath, 'showPreviousValue'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showHistoricalChart)) {
        return errorLogger?.logError({
            message: '"showHistoricalChart" must be undefined or a boolean.',
            value  : settings.showHistoricalChart,
            path   : [...configPath, 'showHistoricalChart'],
        }) ?? false;
    }

    if (
        settings.highlightCardStyle !== undefined
        && !isCardHighlightStyle(settings.highlightCardStyle, errorLogger, [...configPath, 'highlightCardStyle'])
    ) {
        return false;
    }

    // Because this component's settings interface extends MetricsExclusionCustomSettings, we can call "isMetricsExclusionCustomSettings"
    // to validate it
    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Because this component's settings interface extends GridCustomSettings, we can call "isGridCustomSettings" to validate it
    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Because settings is also an EntityNumericMetricsCustomSettings, we can call this type guard function to validate the props defined
    // for "EntityNumericMetricsCustomSettings"
    if (!isEntityNumericMetricsCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // "cardContainerStyle" is optional but if they are provided, they must be objects
    if (settings.cardContainerStyle !== undefined && !isObjectLiteral(settings.cardContainerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.cardContainerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    return true;
};



export interface EntityMetricsCardsViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityAggregatedMetricsCardsViewUIComponentCustomSettings | undefined;
}



/**
 * This is an EntityUIComponent that display all of an entity's metrics in Card view, one card per metric.
 */
export const EntityAggregatedMetricsCardsViewUIComponent: React.FC<EntityMetricsCardsViewUIComponentProps> = (
    props: EntityMetricsCardsViewUIComponentProps,
) => {
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // This component works with TimeSpan. However, if the time value passed to this function is NOT TimeSpan, it will try to convert that
    // to timeSpan values
    const fetchDataParams = useMemo((): FetchEntityTimeSpanDataParams => {

        // If the provided time values is a time span value then it's perfect
        if (isFetchEntityTimeSpanDataParams(props.fetchDataParams)) {
            return props.fetchDataParams;
        }

        // The the provided time value is a Time Range values, convert it to Time Span values. Use the time range's startTime as the
        // time span's time.
        // If time value is not provided then use current time
        const startTime = isFetchEntityTimeRangeDataParams(props.fetchDataParams) ? props.fetchDataParams.startTime : Date.now();
        const defaultTimeSpan = props.customSettings?.defaultTimeSpan ?? TimeSpan.MONTH;
        return {
            timeSpan  : defaultTimeSpan,
            timestamps: [moment(startTime).startOf(defaultTimeSpan).valueOf()],
        };
    }, [props.customSettings?.defaultTimeSpan, props.fetchDataParams]);


    // A local state to keep track of ALL of the entity's metrics' data and status. Each element in this array is an Object that
    // contain the TimeSeries data and status of 1 metric.
    const [metricsData, setMetricsData] = useState<readonly MetricPreviewData[] | undefined>();


    const classes = useStyle();
    const panelClasses = useModePanelStyle();
    const theme = useTheme();


    // Find the entity class object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (entityClasses && selectedEntity) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity?.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);


    /**
     * Once we have the selectedEntityClass, we need to get all the TSDB metric definitions from the entity class, the metrics definition that
     * is type TSDB and has at least 1 numeric metric. All the metrics definition that DOES NOT have at least 1 numeric metrics will be excluded
     */
    const tsdbMetricDefinitions = useMemo(() => {
        const tsdbMetricsDef = selectedEntityClass?.metricsDefinitions?.filter((metricsDef) => {
            return metricsDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB && metricsDef.numericMetrics && metricsDef.numericMetrics.length > 0;
        });

        // filter out metrics definitions based on the customSettings' excludeMetricsDefinitionIds
        const filteredMetricsDef = filterMetricsDefinitionsByMetricsExclusionCustomSettings(
            tsdbMetricsDef ?? [], props.customSettings,
        );

        return filteredMetricsDef;
    }, [props.customSettings, selectedEntityClass]);



    /**
     * Fetch metric data for ALL metrics
     */
    const fetchMetricsData = useCallback(async (): Promise<readonly MetricPreviewData[] | undefined> => {

        if (selectedEntity && tsdbMetricDefinitions) {
            const timeSpan = fetchDataParams.timeSpan;
            const timestamp = fetchDataParams.timestamps[0];

            // Call 'createFetchEntityMetricDataParams' to create the information we need to fetch metric data. This DOES NOT fetch data,
            // it only return array of objects which contains information we need for making API calls later.
            const multipleFetchDataParamsGroups = createFetchEntityMetricDataParamsGroup(
                tsdbMetricDefinitions, selectedEntity, false, props.customSettings, props.customSettings?.defaultAggregation,
            );

            // Compile a list of getEntityMetricGroupPreviewData requests
            const requests: Promise<MetricPreviewData[]>[] = [];

            multipleFetchDataParamsGroups.forEach((group) => {
                // Call getEntityKPIMetricGroupPreviewData to fetch data for group of KPI metrics
                requests.push(getEntityMetricGroupPreviewData(group, timestamp, timeSpan, true,
                    props.customSettings?.showHistoricalChart, props.customSettings?.showPreviousValue,
                ));
            });


            // Resolve all the requests in one call
            const requestResult = await Promise.allSettled(requests).then((allResults) => {
                // Go each result from allResults and get all the result's value for successful result. Return 'undefined' for unsuccessful results
                return allResults.map((singleResult) => {
                    if (singleResult.status === 'fulfilled') {
                        // 'value' is the returned value from the getEntityMetricGroupPreviewData call which will be an array
                        // of MetricPreviewData objects
                        return singleResult.value;
                    }
                    return undefined;
                });
            });

            // Filter out 'undefined' results
            return requestResult
                .flat()                     // requestResult is a double array of MetricPreviewData, 1 per metric so we need to flatten it first
                .filter((result) => {
                    return result !== undefined;
                }) as MetricPreviewData[];
        }

        // Required data is not ready
        return undefined;
    }, [selectedEntity, tsdbMetricDefinitions, fetchDataParams.timeSpan, fetchDataParams.timestamps, props.customSettings]);



    /**
     * This useEffect is used for fetching data for all metrics of an Entity
     */
    useEffect(() => {
        fetchMetricsData().then((data) => {
            setMetricsData(data);
        });
    }, [fetchMetricsData]);



    /**
     * Get the color to use for the sparkline chart based on the KPIStatus value
     */
    const getChartColor = useCallback((status: KPIStatus): string => {
        if (props.customSettings?.highlightCardStyle === CardHighlightStyle.BACKGROUND) {
            // If the highlight style is background, the background is already colored therefore we need to use the Text color instead
            if (status === KPIStatus.GOOD) {
                return theme.palette.success.contrastText;
            }
            if (status === KPIStatus.BAD) {
                return theme.palette.error.contrastText;
            }
            return theme.palette.text.primary;
        }
        if (status === KPIStatus.GOOD) {
            return theme.palette.success.main;
        }
        if (status === KPIStatus.BAD) {
            return theme.palette.error.main;
        }
        return theme.palette.text.primary;
    }, [props.customSettings?.highlightCardStyle, theme.palette.error.contrastText, theme.palette.error.main, theme.palette.success.contrastText,
        theme.palette.success.main, theme.palette.text.primary]);


    return (
        <>
            {selectedEntity && selectedEntityClass && metricsData && (
                <ExtGrid
                    className={clsx(props.className, classes.root)}
                    container
                    xs_spacing={1}
                    md_spacing={2}
                    lg_spacing={3}
                >
                    {metricsData.map((metricData) => {
                        const chartColor = getChartColor(metricData.status);
                        const isKPI = isKPIMetric(metricData.metric);

                        const numericMetricSettings = getNumericMetricCustomSettings(
                            metricData.metric.name,
                            metricData.metricDefinition.metricsDefinitionId,
                            props.customSettings,
                        );
                        const decimalPlaces = numericMetricSettings?.decimalPlaces;

                        // Get the difference between the currentValue and previousValue if props.customSettings?.showPreviousValue
                        // is true and we have values for both
                        const metricValueDiff = props.customSettings?.showPreviousValue && metricData.previousValue !== undefined
                            && metricData.currentValue !== undefined ? metricData.currentValue - metricData.previousValue : 0;

                        return (
                            <Grid
                                item
                                key={`${metricData.metricDefinition.metricsDefinitionId}-${metricData.metric.name}`}
                                xs={props.customSettings?.gridSizes?.xs ?? 12}
                                sm={props.customSettings?.gridSizes?.sm}
                                md={props.customSettings?.gridSizes?.md}
                                lg={props.customSettings?.gridSizes?.lg}
                                xl={props.customSettings?.gridSizes?.xl}
                            >
                                <Paper
                                    className={clsx(
                                        panelClasses.root,
                                        classes.metricCard,
                                        // only highlight the card if the metric is KPI metric.
                                        isKPI && 'highlight',
                                        props.customSettings?.highlightCardStyle ?? CardHighlightStyle.BAR,
                                        `status-${metricData.status.toLowerCase()}`,
                                    )}
                                    style={props.customSettings?.cardContainerStyle}
                                    elevation={2}
                                >
                                    <div className={clsx(panelClasses.panelContent, classes.panelContent)}>
                                        <div className={classes.metricInfo}>
                                            <div className={classes.metricName}>
                                                {metricData.metric.evaluator?.displayName || metricData.metric.displayName || metricData.metric.name}
                                            </div>
                                            {props.customSettings?.showPreviousValue && (
                                                <>
                                                    {metricValueDiff > 0 && <Icon>arrow_upward</Icon>}
                                                    {metricValueDiff < 0 && <Icon>arrow_downward</Icon>}
                                                    {metricValueDiff === 0 && <Icon>remove</Icon>}
                                                </>
                                            )}
                                        </div>
                                        <div className={clsx(
                                            classes.metricChartAndValueContainer,
                                            props.customSettings?.showHistoricalChart && 'has-chart',
                                        )}
                                        >
                                            {props.customSettings?.showHistoricalChart && (
                                                <div className="chart-col">
                                                    {metricData.dataPoints.length > 0 && (
                                                        <SparklineChart
                                                            className="chart"
                                                            dataPoints={metricData.dataPoints}
                                                            metrics={metricData.metric}
                                                            color={chartColor}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            <div className="value-col">
                                                {metricData.currentValue !== undefined
                                                    ? trans('{{value, number-format}}', {
                                                        value: metricData.currentValue,
                                                        decimalPlaces,
                                                    }) : NO_VALUE}
                                            </div>
                                            <div className="unit-col">
                                                {metricData.metric.unit}
                                            </div>
                                        </div>
                                        {props.customSettings?.showPreviousValue && (
                                            <div className={classes.previousMetricValueDisplay}>
                                                {trans(
                                                    `entities.entity_aggregated_metrics_cards_comp.previous_value_display.${fetchDataParams.timeSpan}`,
                                                    {
                                                        value: trans('label.value_unit_display', {
                                                            value: metricData.previousValue !== undefined
                                                                ? trans('{{value, number-format}}', {
                                                                    value: metricData.previousValue,
                                                                    decimalPlaces,
                                                                }) : NO_VALUE,
                                                            unit: metricData.metric.unit,
                                                        }),
                                                    })}
                                            </div>
                                        )}
                                    </div>
                                </Paper>
                            </Grid>
                        );
                    })}
                </ExtGrid>
            )}

            {!(selectedEntity || selectedEntityClass) && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntityAggregatedMetricsCardsViewUIComponent', EntityAggregatedMetricsCardsViewUIComponent);


// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Aggregated_Metrics_Cards_Comp',
    {
        componentFunc          : EntityAggregatedMetricsCardsViewUIComponent,
        customSettingsValidator: isEntityAggregatedMetricsCardsViewUIComponentCustomSettings,
    },
);
